import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, TextField, Button, Divider, Stack,
  Accordion, AccordionSummary, AccordionDetails, CircularProgress,
  MenuItem, FormControl, InputLabel, Select,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import { POSCliente } from './types';
import { formatCurrency } from '../../../utils/formatters';
import UbicacionSelector from '../../../components/UbicacionSelector';
import vendedorService, { Vendedor } from '../../../services/vendedorService';

export interface POSSucursalOption {
  nroSuc: number;
  nombreSucursal?: string;
}

interface Props {
  subtotalNeto: number;
  porcDesc: number;
  porcDesc2: number;
  porcDesc3: number;
  onPorcDescChange: (field: 'porcDesc' | 'porcDesc2' | 'porcDesc3', value: number) => void;
  iva: number;
  total: number;
  observaciones: string;
  onObservacionesChange: (v: string) => void;
  cliente: POSCliente | null;
  /** Fecha de emisión en formato YYYY-MM-DD (input type="date") */
  fechaEmision: string;
  onFechaEmisionChange: (ymd: string) => void;
  nroVendedor: number;
  /** Si el nro está informado pero no viene en el catálogo, se muestra este nombre (p. ej. usuario legado). */
  nombreVendedorFallback?: string;
  onNroVendedorChange: (nroVendedor: number, nombreVendedor?: string) => void;
  formaDePago: string;
  onFormaDePagoChange: (v: string) => void;
  ordenCompra: string;
  onOrdenCompraChange: (v: string) => void;
  transporte: string;
  onTransporteChange: (v: string) => void;
  ubicacion: string;
  onUbicacionChange: (v: string) => void;
  saving: boolean;
  onGuardar: (emitir: boolean) => void;
  cantLineas: number;
  readOnly?: boolean;
  saveLabel?: string;
  hideDraftButton?: boolean;
  sucursales?: POSSucursalOption[];
  nroSuc?: number;
  onNroSucChange?: (n: number) => void;
  loadingSucursales?: boolean;
}

const FORMAS_DE_PAGO = ['CONTADO', 'CTA.CTE.', 'EFECTIVO', 'CONTRAREEMBOLSO'];

const TotalRow: React.FC<{
  label: string;
  value: number;
  bold?: boolean;
  large?: boolean;
  negative?: boolean;
}> = ({ label, value, bold, large, negative }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" py={0.25}>
    <Typography
      variant={large ? 'body1' : 'body2'}
      fontWeight={bold ? 700 : 400}
      color={large ? 'text.primary' : 'text.secondary'}
    >
      {label}
    </Typography>
    <Typography
      variant={large ? 'body1' : 'body2'}
      fontWeight={bold ? 700 : 400}
      color={negative ? 'error.main' : large ? 'primary.main' : 'text.primary'}
    >
      {negative && value !== 0 ? `−${formatCurrency(value)}` : formatCurrency(value)}
    </Typography>
  </Stack>
);

const FacturaPOSTotalesPanel: React.FC<Props> = ({
  subtotalNeto,
  porcDesc, porcDesc2, porcDesc3,
  onPorcDescChange,
  iva, total,
  observaciones, onObservacionesChange,
  cliente,
  fechaEmision, onFechaEmisionChange,
  nroVendedor,
  nombreVendedorFallback,
  onNroVendedorChange,
  formaDePago, onFormaDePagoChange,
  ordenCompra, onOrdenCompraChange,
  transporte, onTransporteChange,
  ubicacion, onUbicacionChange,
  saving, onGuardar, cantLineas,
  readOnly = false,
  saveLabel,
  hideDraftButton = false,
  sucursales,
  nroSuc = 0,
  onNroSucChange,
  loadingSucursales = false,
}) => {
  const [showTotales, setShowTotales] = useState(true);
  const [showEmision, setShowEmision] = useState(false);
  const [showComercial, setShowComercial] = useState(false);
  const [showEntrega, setShowEntrega] = useState(false);
  const [vendedoresLista, setVendedoresLista] = useState<Vendedor[]>([]);
  const [loadingVendedores, setLoadingVendedores] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingVendedores(true);
      try {
        const list = await vendedorService.getVendedores();
        if (!cancelled) setVendedoresLista(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setVendedoresLista([]);
      } finally {
        if (!cancelled) setLoadingVendedores(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const vendedoresOpciones = useMemo((): Vendedor[] => {
    const sorted = [...vendedoresLista].sort((a, b) =>
      String(a.nombreVendedor ?? '').localeCompare(String(b.nombreVendedor ?? ''), 'es'),
    );
    const n = Number(nroVendedor) || 0;
    if (n > 0 && !sorted.some((v) => v.nroVendedor === n)) {
      return [
        {
          nroVendedor: n,
          nombreVendedor: (nombreVendedorFallback ?? '').trim() || `Vendedor ${n}`,
        },
        ...sorted,
      ];
    }
    return sorted;
  }, [vendedoresLista, nroVendedor, nombreVendedorFallback]);

  const importeDesc1 = subtotalNeto * porcDesc / 100;
  const baseDesc2 = subtotalNeto - importeDesc1;
  const importeDesc2 = baseDesc2 * porcDesc2 / 100;
  const baseDesc3 = baseDesc2 - importeDesc2;
  const importeDesc3 = baseDesc3 * porcDesc3 / 100;

  const canSave = !!(cliente && cantLineas > 0) && !readOnly;

  const discountFieldSx = {
    '& .MuiInputBase-input': { textAlign: 'right', fontSize: '0.82rem', padding: '6px 8px' },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Totales del documento (colapsable, expandido por defecto) */}
      <Accordion
        expanded={showTotales}
        onChange={() => setShowTotales((p) => !p)}
        variant="outlined"
        disableGutters
        sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 0 } }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CalculateOutlinedIcon fontSize="small" color="action" />
            <Typography variant="body2">Totales del documento</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
          <TotalRow label="Subtotal" value={subtotalNeto} />

          <Box mt={1} mb={0.5}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>
              Descuentos globales
            </Typography>
            <Stack direction="row" spacing={1}>
              {(['porcDesc', 'porcDesc2', 'porcDesc3'] as const).map((field, i) => {
                const val = [porcDesc, porcDesc2, porcDesc3][i];
                return (
                  <TextField
                    key={field}
                    label={`Desc ${i + 1}%`}
                    size="small"
                    type="number"
                    value={val || ''}
                    onChange={(e) => onPorcDescChange(field, parseFloat(e.target.value) || 0)}
                    inputProps={{ min: 0, max: 100 }}
                    sx={{ flex: 1, ...discountFieldSx }}
                  />
                );
              })}
            </Stack>
            {(porcDesc > 0 || porcDesc2 > 0 || porcDesc3 > 0) && (
              <Box mt={0.75}>
                {porcDesc > 0 && (
                  <TotalRow label={`Desc 1 (${porcDesc}%)`} value={importeDesc1} negative />
                )}
                {porcDesc2 > 0 && (
                  <TotalRow label={`Desc 2 (${porcDesc2}%)`} value={importeDesc2} negative />
                )}
                {porcDesc3 > 0 && (
                  <TotalRow label={`Desc 3 (${porcDesc3}%)`} value={importeDesc3} negative />
                )}
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 1 }} />
          <TotalRow label="IVA (21%)" value={iva} />
          <Divider sx={{ my: 1 }} />
          <TotalRow label="TOTAL" value={total} bold large />
          <TextField
            label="Observaciones"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={observaciones}
            onChange={(e) => onObservacionesChange(e.target.value)}
            disabled={readOnly}
            sx={{ mt: 1.5 }}
          />
        </AccordionDetails>
      </Accordion>

      {/* Datos de emisión (colapsable) */}
      <Accordion
        expanded={showEmision}
        onChange={() => setShowEmision((p) => !p)}
        variant="outlined"
        disableGutters
        sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 0 } }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CalendarTodayIcon fontSize="small" color="action" />
            <Typography variant="body2">Datos de emisión</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Stack spacing={1.5}>
            <TextField
              label="Fecha de emisión"
              type="date"
              size="small"
              fullWidth
              value={fechaEmision}
              onChange={(e) => onFechaEmisionChange(e.target.value)}
              disabled={readOnly}
              InputLabelProps={{ shrink: true }}
            />
            {onNroSucChange != null && (
              loadingSucursales ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <CircularProgress size={22} />
                </Box>
              ) : sucursales != null && sucursales.length > 0 ? (
                <FormControl size="small" fullWidth>
                  <InputLabel>Sucursal</InputLabel>
                  <Select
                    value={nroSuc || ''}
                    label="Sucursal"
                    onChange={(e) => onNroSucChange(Number(e.target.value))}
                    disabled={readOnly}
                  >
                    {sucursales.map((s) => (
                      <MenuItem key={s.nroSuc} value={s.nroSuc}>
                        {s.nombreSucursal ? `${s.nroSuc} — ${s.nombreSucursal}` : `Suc. ${s.nroSuc}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Vendedor y condiciones (colapsable, mismo patrón que Entrega / Logística) */}
      <Accordion
        expanded={showComercial}
        onChange={() => setShowComercial((p) => !p)}
        variant="outlined"
        disableGutters
        sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 0 } }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AssignmentIndIcon fontSize="small" color="action" />
            <Typography variant="body2">Vendedor y condiciones</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Stack spacing={1.5}>
            {loadingVendedores ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={22} />
              </Box>
            ) : (
              <FormControl size="small" fullWidth>
                <InputLabel id="factura-pos-vendedor-label" shrink>
                  Vendedor
                </InputLabel>
                <Select
                  labelId="factura-pos-vendedor-label"
                  label="Vendedor"
                  value={nroVendedor > 0 ? nroVendedor : ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      onNroVendedorChange(0);
                      return;
                    }
                    const num = Number(raw);
                    const v = vendedoresOpciones.find((x) => x.nroVendedor === num);
                    onNroVendedorChange(num, v?.nombreVendedor);
                  }}
                  disabled={readOnly}
                  displayEmpty
                  renderValue={(selected: number | string) => {
                    if (selected === '' || selected == null) {
                      return (
                        <Box component="span" sx={{ color: 'text.secondary' }}>
                          Seleccionar
                        </Box>
                      );
                    }
                    const v = vendedoresOpciones.find((x) => x.nroVendedor === Number(selected));
                    if (!v) return String(selected);
                    const nom = (v.nombreVendedor ?? '').trim();
                    return nom ? `${nom} (${v.nroVendedor})` : `Vendedor ${v.nroVendedor}`;
                  }}
                >
                  <MenuItem value="">
                    <em>Seleccionar</em>
                  </MenuItem>
                  {vendedoresOpciones.map((v) => (
                    <MenuItem key={v.nroVendedor} value={v.nroVendedor}>
                      {(v.nombreVendedor ?? '').trim()
                        ? `${v.nombreVendedor} (${v.nroVendedor})`
                        : `Vendedor ${v.nroVendedor}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              select
              label="Forma de pago"
              size="small"
              fullWidth
              value={formaDePago || ''}
              onChange={(e) => onFormaDePagoChange(e.target.value)}
              disabled={readOnly}
              InputLabelProps={{ shrink: true }}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected) => {
                  const s = String(selected ?? '').trim();
                  if (!s) {
                    return (
                      <Box component="span" sx={{ color: 'text.secondary' }}>
                        Seleccionar
                      </Box>
                    );
                  }
                  return s;
                },
              }}
            >
              <MenuItem value="">
                <em>Seleccionar</em>
              </MenuItem>
              {FORMAS_DE_PAGO.map((f) => (
                <MenuItem key={f} value={f}>
                  {f}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Orden de compra"
              size="small"
              fullWidth
              value={ordenCompra}
              onChange={(e) => onOrdenCompraChange(e.target.value)}
              disabled={readOnly}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Entrega colapsable */}
      <Accordion
        expanded={showEntrega}
        onChange={() => setShowEntrega((p) => !p)}
        variant="outlined"
        disableGutters
        sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 0 } }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LocalShippingIcon fontSize="small" color="action" />
            <Typography variant="body2">Entrega / Logística</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Stack spacing={1.5}>
            <TextField
              label="Transporte / Expreso"
              size="small"
              fullWidth
              value={transporte}
              onChange={(e) => onTransporteChange(e.target.value)}
              disabled={readOnly}
            />
            <UbicacionSelector
              value={ubicacion}
              onChange={(v) => onUbicacionChange(v)}
              disabled={readOnly}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      {!readOnly && (
        <Stack spacing={1}>
          {!hideDraftButton && (
            <Button
              variant="outlined"
              fullWidth
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={() => onGuardar(false)}
              disabled={saving || !canSave}
              sx={{ textTransform: 'none' }}
            >
              Guardar borrador
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            fullWidth
            startIcon={saving ? <CircularProgress size={16} /> : <ReceiptIcon />}
            onClick={() => onGuardar(true)}
            disabled={saving || !canSave}
            sx={{ py: 1.2, fontWeight: 600, textTransform: 'none' }}
          >
            {saveLabel ?? 'Emitir factura'}
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export default FacturaPOSTotalesPanel;
